import { Analytics } from '@cobliteam/analytics';
import { captureScreenTraits } from '@cobliteam/analytics/src/screenTraits';
import type { UserAttributes } from '@cobliteam/api-lib';

import type { Session, UserData } from '@cobliteam/common-types';
import { UserRole } from '@cobliteam/common-types';
// root package.json
import datadogRUM, { DatadogTrack } from '@cobliteam/datadog-rum';
import type { FeatureFlags } from '@cobliteam/hooks/src/useFeatureFlags';
import useAuthStore from '@cobliteam/hooks/src/useSession/store/useAuthStore';
import { Notifications } from '@cobliteam/onesignal';
import { Environment } from '@cobliteam/react-environments';
import { runAfterPageSettles } from '@cobliteam/utils/idle';
import { clarity } from 'react-microsoft-clarity';
import packageJSON from '../../../../package.json';

const env = Environment.getInstance().getEnvironment();

// Os SDKs abaixo reenviam identify/login/init a cada chamada; só a mudança de identidade justifica repetir.
let lastIdentityKey: string | undefined;
let hasClarityInitialized = false;

type ThirdPartyInput = { session: Session; userData?: UserData; featureFlags: FeatureFlags };

const getIdentityKey = ({ session, userData, featureFlags }: ThirdPartyInput) =>
  JSON.stringify([
    session.user.id,
    session.user.role,
    session.fleet.id,
    session.fleet.name,
    // Os três atributos vão como traits no Segment e no Datadog.
    userData?.userAttributes?.numberOfDevices,
    userData?.userAttributes?.creationDate,
    userData?.userAttributes?.ageInDays,
    featureFlags.oneSignal,
    featureFlags.useDdRum,
    featureFlags.ddRumSampleRate,
    featureFlags.ddRumReplaySampleRate,
    featureFlags.ddRumProfilingSampleRate,
    featureFlags.sidebar,
    featureFlags.aiChat,
  ]);

export const configureOneSignal = ({ user, fleet }: Session) => {
  const isLocalhost = window.location.href.includes('localhost');
  if (!isLocalhost) Notifications.initialize(fleet.id, fleet.name, user.id);
};

export const configureAnalytics = (session: Session, featureFlags: FeatureFlags, userAttributes?: UserAttributes | null) => {
  // Identificar roda uma vez por identidade: se o Segment ainda não carregou, o identify/group se perderia.
  if (!Analytics.isInitialized()) Analytics.initialize();
  Analytics.setUser(session, featureFlags, userAttributes);
};

export const configureDatadogRUM = (session: Session, sampleRate: number, replaySampleRate: number, profilingSampleRate: number, userAttributes?: UserAttributes | null) => {
  const isLocalhost = window.location.href.includes('localhost');
  const isPreview = window.location.href.includes('dashboard.preview.cobli.co');

  // when running locally or in preview, we don't want to send any data to datadog
  const envSampleRate = isLocalhost || isPreview ? 0 : sampleRate;
  const envReplaySampleRate = isLocalhost || isPreview ? 0 : replaySampleRate;
  const envProfilingSampleRate = isLocalhost || isPreview ? 0 : profilingSampleRate;

  const configuration = {
    service: 'herbie-dashboard',
    env: env.name,
    version: packageJSON.version,
    sessionSampleRate: envSampleRate,
    sessionReplaySampleRate: envReplaySampleRate,
    profilingSampleRate: envProfilingSampleRate,
    trackResources: true,
    trackLongTasks: true,
    traceIntegration: true,
    trackFrustrations: true,
    trackUserInteractions: true,
  };

  const user = {
    id: session?.user?.id,
    name: session?.user?.name,
    email: session?.user?.email,
    role: session?.user?.role,
    app_version: configuration.version,
    fleet: {
      id: session?.fleet?.id,
      name: session?.fleet?.name,
      num_devices: userAttributes?.numberOfDevices,
      creation_date: userAttributes?.creationDate,
      age_in_days: userAttributes?.ageInDays,
    },
  };

  datadogRUM({ configuration, user });

  // Global context lands on every RUM event (error, long task, rage click), which is what turns
  // "how many users have a cramped viewport" into "does this screen actually break for them".
  DatadogTrack.setGlobalContextProperty('screen', captureScreenTraits());
};

export const configureClarity = ({ user, fleet }: Session, featureFlags: FeatureFlags) => {
  if (env.clarityId) {
    // Cada init injeta outro <script> do Clarity.
    if (!hasClarityInitialized) {
      clarity.init(env.clarityId);
      hasClarityInitialized = true;
    }

    if (clarity.hasStarted()) {
      clarity.setTag('fleet_id', fleet.id);
      clarity.setTag('fleet_name', fleet.name);
      clarity.setTag('user_id', user.id);
      clarity.setTag('user_email', user.email);
      clarity.setTag('version', window.__DASHBOARD_MONOREPO_VERSION__);
      // Clarity already filters by screen resolution; zoom and the viewport bucket are the gap
      // between "small monitor" and "magnified screen" when hunting for a replay.
      const screenTraits = captureScreenTraits();
      clarity.setTag('device_pixel_ratio', String(screenTraits.devicePixelRatio));
      clarity.setTag('browser_zoom', String(screenTraits.browserZoom ?? 'unknown'));
      clarity.setTag('viewport_width_bucket', screenTraits.viewportWidthBucket);
      clarity.setTag('sidebar', featureFlags.sidebar ? 'true' : 'false');
      clarity.setTag('ai-chat', featureFlags.aiChat ? 'true' : 'false');
      clarity.identify(user.id, {
        email: user.email,
        username: user.name,
        phone_number: user.phoneNumber,
        fleet_id: fleet.id,
        fleet_name: fleet.name,
        version: window.__DASHBOARD_MONOREPO_VERSION__,
      });

      clarity.upgrade('Session started with data user');
    }
  }
};

export const initializeThirdParty = ({ session, userData, featureFlags }: ThirdPartyInput) => {
  if (process.env.REACT_APP_DEMO_MODE === 'true') {
    return;
  }

  const identityKey = getIdentityKey({ session, userData, featureFlags });
  if (identityKey === lastIdentityKey) {
    return;
  }
  lastIdentityKey = identityKey;

  const userAttributes = userData?.userAttributes;

  // analytics segment
  configureAnalytics(session, featureFlags, userAttributes);

  // oneSignal
  if (session.user.role !== UserRole.MAP_VIEW && featureFlags.oneSignal) {
    configureOneSignal(session);
  }

  // datadogRUM
  if (featureFlags.useDdRum && featureFlags.ddRumSampleRate) {
    configureDatadogRUM(session, featureFlags.ddRumSampleRate, featureFlags.ddRumReplaySampleRate, featureFlags.ddRumProfilingSampleRate ?? 0, userAttributes);
  }

  // clarity: replay só precisa estar gravando depois que a rota inicial assenta
  runAfterPageSettles(() => configureClarity(session, featureFlags));
};

/**
 * Logout e login são SPA e o logout zera os SDKs (`Analytics.reset()`): sem esquecer a identidade,
 * quem entra de novo com o mesmo usuário e frota nunca é reidentificado e fica anônimo.
 */
export const resetThirdPartyIdentityOnLogout = () =>
  useAuthStore.subscribe((state, previousState) => {
    if (previousState.session && !state.session) lastIdentityKey = undefined;
  });

export const resetThirdPartyIdentityForTests = () => {
  lastIdentityKey = undefined;
  hasClarityInitialized = false;
};
